<template>
  <v-dialog v-model="isOpen" max-width="480" persistent data-cy="create-trial-from-lead-modal">
    <v-card>
      <v-card-title class="pa-4 d-flex align-center justify-space-between">
        <span>Create Trial</span>
        <v-btn icon variant="text" size="small" @click="close" :disabled="loading">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider />

      <v-card-text class="pa-4">
        <div class="mb-4 pa-3 bg-grey-lighten-4 rounded">
          <div class="text-caption text-grey mb-1">Lead</div>
          <div class="text-body-2 font-weight-medium">
            {{ props.lead?.firstname }} {{ props.lead?.lastname }}
          </div>
          <div class="text-body-2">{{ props.lead?.school_name }}</div>
          <div class="text-caption text-grey">{{ props.lead?.email }}</div>
        </div>

        <v-alert type="warning" density="compact" variant="tonal" class="mb-2">
          This will approve the lead, stamp a trial date, and send an activation email.
          This cannot be undone from this screen.
        </v-alert>
      </v-card-text>

      <v-divider />

      <v-card-actions class="pa-4">
        <v-spacer />
        <v-btn
          variant="outlined"
          color="grey"
          @click="close"
          :disabled="loading"
          data-cy="cancel-create-trial-btn"
        >
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          variant="flat"
          :loading="loading"
          @click="confirm"
          data-cy="confirm-create-trial-btn"
        >
          Create Trial
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import { useAlertsStore } from "@/stores/alerts";
import useLeads from "@/stores/support/leads";
import type { TrialMembership } from "@/stores/support/trials";

const props = defineProps<{
  modelValue: boolean;
  lead: TrialMembership | null;
}>();

const emit = defineEmits<{
  "update:modelValue": [value: boolean];
  converted: [];
}>();

const leadsStore = useLeads();
const alertsStore = useAlertsStore();
const loading = ref(false);

const isOpen = computed({
  get: () => props.modelValue,
  set: (val) => emit("update:modelValue", val),
});

const close = () => {
  isOpen.value = false;
};

const confirm = async () => {
  if (!props.lead) return;
  loading.value = true;
  try {
    const res = await leadsStore.createTrialFromLead(props.lead.trial_id);
    if (res.status) {
      alertsStore.addAlert(
        "success",
        res.message || "Trial approved — activation email sent",
        5
      );
      emit("converted");
      close();
    } else {
      alertsStore.addAlert("error", res.message || "Failed to create trial", 10);
    }
  } catch (e) {
    console.error("createTrialFromLead error:", e);
    alertsStore.addAlert("error", "An unexpected error occurred.", 10);
  } finally {
    loading.value = false;
  }
};
</script>
